import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import SectionLabel from "../ui/SectionLabel";

const salaryOptions = [
  { label: "₹30K", value: 30000 },
  { label: "₹50K", value: 50000 },
  { label: "₹80K", value: 80000 },
  { label: "₹1.2L+", value: 120000 },
];

/* ── Animated number that counts up when value changes ── */
function AnimatedValue({ value, formatter }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);

  useEffect(() => {
    const from = prev.current;
    const to = value;
    prev.current = value;
    if (from === to) return;
    const duration = 600;
    const start = performance.now();
    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [value]);

  return <>{formatter(display)}</>;
}

export default function ROICalculator() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.2 });

  const [employees, setEmployees] = useState(87);
  const [avgSalary, setAvgSalary] = useState(50000);
  const [manualHours, setManualHours] = useState(9);

  const hourlyRate = avgSalary / (22 * 8);
  const weeklyWaste = employees * manualHours * hourlyRate;
  const monthlyWaste = weeklyWaste * 4.33;
  const yearlyWaste = monthlyWaste * 12;
  const hoursRecoverable = Math.round(employees * manualHours * 0.85);

  const formatCurrency = (val) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
    return `₹${Math.round(val)}`;
  };

  return (
    <section id="calculator" ref={sectionRef} className="roi-calc-section">
      {/* Dot pattern overlay */}
      <div className="roi-dots-pattern" />

      <div className="roi-calc-container">
        <div style={{ textAlign: "center" }}>
          <SectionLabel text="ROI CALCULATOR" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", marginBottom: "48px" }}
        >
          <h2 className="roi-calc-heading">
            Manual work is
            <br />
            <span className="roi-heading-white">a silent tax.</span>
          </h2>
          <p className="roi-calc-sub">
            Every report typed, every approval chased, every spreadsheet updated by hand — quietly bleeding your payroll. Move the sliders. See your bill.
          </p>
        </motion.div>

        {/* ── Single parent card wrapping everything ── */}
        <motion.div
          className="roi-parent-card"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <div className="roi-calc-grid">
            {/* ── Left: Inputs + CTA ── */}
            <div className="roi-left">
              <div className="roi-inputs">
                {/* Employees */}
                <div className="roi-field">
                  <div className="roi-field-header">
                    <label className="roi-label">Number of employees</label>
                    <span className="roi-field-val">{employees}</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    step="1"
                    value={employees}
                    onChange={(e) => setEmployees(Number(e.target.value))}
                    className="roi-slider"
                    style={{ "--fill": `${((employees - 10) / 490) * 100}%` }}
                  />
                </div>

                {/* Average Salary */}
                <div className="roi-field">
                  <label className="roi-label">Average monthly salary</label>
                  <div className="roi-salary-row">
                    {salaryOptions.map((opt) => (
                      <button
                        key={opt.value}
                        className={`roi-sal-btn${avgSalary === opt.value ? " roi-sal-active" : ""}`}
                        onClick={() => setAvgSalary(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Manual hours */}
                <div className="roi-field">
                  <div className="roi-field-header">
                    <label className="roi-label">
                      Hours/week on automatable tasks{" "}
                      <span className="roi-label-hint">(per person)</span>
                    </label>
                    <span className="roi-field-val">{manualHours}h</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="35"
                    step="1"
                    value={manualHours}
                    onChange={(e) => setManualHours(Number(e.target.value))}
                    className="roi-slider"
                    style={{ "--fill": `${((manualHours - 1) / 34) * 100}%` }}
                  />
                </div>
              </div>

              {/* CTA */}
              <div className="roi-card-cta">
                <p className="roi-cta-text">
                  Want to see exactly where the money is going?
                </p>
                <a href="/resource-optimizer/book" className="roi-cta-btn">
                  Book your free audit
                  <ArrowRight size={16} strokeWidth={2} />
                </a>
              </div>
            </div>

            {/* ── Right: Results ── */}
            <div className="roi-right">
              <div className="roi-waste-card">
                <div className="roi-waste-label">YOUR ESTIMATED WASTE</div>
                <div className="roi-waste-value">
                  <AnimatedValue value={monthlyWaste} formatter={formatCurrency} />
                </div>
                <div className="roi-waste-sub">wasted every month</div>
                <div className="roi-waste-yearly">
                  That's{" "}
                  <span className="roi-yearly-accent">
                    <AnimatedValue value={yearlyWaste} formatter={formatCurrency} />/year
                  </span>{" "}
                  down the drain
                </div>
              </div>

              <div className="roi-stats-row">
                <div className="roi-stat-card">
                  <div className="roi-stat-val">
                    <AnimatedValue
                      value={hoursRecoverable}
                      formatter={(v) => `${Math.round(v)}h`}
                    />
                  </div>
                  <div className="roi-stat-lbl">
                    recoverable
                    <br />
                    hours/week
                  </div>
                </div>
                <div className="roi-stat-card">
                  <div className="roi-stat-val">85%</div>
                  <div className="roi-stat-lbl">
                    typically
                    <br />
                    automatable
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        /* ── Section ── */
        .roi-calc-section {
          position: relative;
          background: #2BB5AE !important;
          padding: 80px var(--container-padding);
          overflow: hidden;
        }

        .roi-dots-pattern {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.35) 1.8px, transparent 1.8px);
          background-size: 24px 24px;
          pointer-events: none;
          -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, transparent 40%, black 80%);
          mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, transparent 40%, black 80%);
        }

        .roi-calc-container {
          max-width: 1060px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        /* Override section-label on teal bg */
        .roi-calc-section .section-label {
          background: rgba(0,0,0,0.1) !important;
          border-color: rgba(0,0,0,0.08) !important;
          color: #fff !important;
        }
        .roi-calc-section .section-label::before {
          background: #fff !important;
          box-shadow: 0 0 6px rgba(255,255,255,0.3) !important;
        }

        /* ── Heading ── */
        .roi-calc-heading {
          font-weight: 900;
          font-size: clamp(36px, 5vw, 60px);
          color: #1a1a2e;
          line-height: 1.15;
          letter-spacing: -0.03em;
          margin: 16px 0 18px;
        }

        .roi-heading-white {
          color: #fff;
        }

        .roi-calc-sub {
          font-size: 15px;
          color: #fff;
          line-height: 1.6;
          margin: 0;
          font-weight: 400;
        }

        /* ── Parent card ── */
        .roi-parent-card {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 6px 40px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        /* ── Grid inside parent card ── */
        .roi-calc-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          min-height: 0;
        }

        /* ── Left side ── */
        .roi-left {
          padding: 32px 28px;
          display: flex;
          flex-direction: column;
        }

        .roi-inputs {
          display: flex;
          flex-direction: column;
          gap: 22px;
          flex: 1;
        }

        .roi-field-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .roi-label {
          font-size: 13px;
          font-weight: 600;
          color: #222;
        }

        .roi-label-hint {
          font-weight: 400;
          color: #aaa;
          font-size: 11.5px;
        }

        .roi-field-val {
          font-weight: 700;
          font-size: 17px;
          color: #2BB5AE;
          min-width: 36px;
          text-align: right;
        }

        /* ── Slider ── */
        .roi-slider {
          width: 100%;
          height: 5px;
          -webkit-appearance: none;
          appearance: none;
          border-radius: 5px;
          outline: none;
          cursor: pointer;
          background: linear-gradient(
            90deg,
            #2BB5AE 0%,
            #2BB5AE var(--fill, 50%),
            #e8e8e8 var(--fill, 50%),
            #e8e8e8 100%
          );
        }

        .roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #2BB5AE;
          cursor: pointer;
          box-shadow: 0 1px 6px rgba(40, 180, 174, 0.3);
          transition: box-shadow 0.2s ease;
        }

        .roi-slider::-webkit-slider-thumb:hover {
          box-shadow: 0 2px 10px rgba(40, 180, 174, 0.45);
        }

        .roi-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #2BB5AE;
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 6px rgba(40, 180, 174, 0.3);
        }

        /* ── Salary buttons ── */
        .roi-salary-row {
          display: flex;
          gap: 8px;
          margin-top: 10px;
        }

        .roi-sal-btn {
          flex: 1;
          padding: 11px 8px;
          border-radius: 10px;
          border: 1.5px solid #e2e2e2;
          background: #fff;
          font-size: 13.5px;
          font-weight: 600;
          color: #888;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .roi-sal-btn:hover {
          border-color: #2BB5AE;
          color: #2BB5AE;
        }

        .roi-sal-active {
          background: #2BB5AE;
          border-color: #2BB5AE;
          color: #fff;
        }

        .roi-sal-active:hover {
          color: #fff;
        }

        /* ── CTA ── */
        .roi-card-cta {
          padding-top: 24px;
          margin-top: 24px;
          border-top: 1px solid #eee;
          text-align: center;
        }

        .roi-cta-text {
          font-size: 13px;
          color: #aaa;
          margin: 0 0 14px;
          font-weight: 400;
        }

        .roi-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: #2BB5AE;
          color: #fff;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .roi-cta-btn:hover {
          background: #24a09a;
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
          color: #fff;
        }

        /* ── Right side ── */
        .roi-right {
          background: #f3f9f9;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .roi-waste-card {
          background: #fff;
          border-radius: 14px;
          padding: 26px 20px;
          text-align: center;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .roi-waste-label {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.1em;
          color: #999;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .roi-waste-value {
          font-weight: 900;
          font-size: clamp(36px, 4vw, 48px);
          color: #1a1a2e;
          line-height: 1;
          letter-spacing: -0.04em;
          margin-bottom: 4px;
        }

        .roi-waste-sub {
          font-size: 13px;
          color: #999;
          margin-bottom: 14px;
          font-weight: 400;
        }

        .roi-waste-yearly {
          font-size: 13px;
          color: #777;
          padding-top: 12px;
          border-top: 1px solid #eee;
          font-weight: 400;
        }

        .roi-yearly-accent {
          color: #2BB5AE;
          font-weight: 700;
        }

        /* ── Bottom stats ── */
        .roi-stats-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .roi-stat-card {
          background: #fff;
          border-radius: 12px;
          padding: 20px 12px;
          text-align: center;
        }

        .roi-stat-val {
          font-weight: 800;
          font-size: 24px;
          color: #1a1a2e;
          letter-spacing: -0.03em;
          margin-bottom: 4px;
        }

        .roi-stat-lbl {
          font-size: 11px;
          color: #999;
          line-height: 1.35;
          font-weight: 400;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .roi-calc-section {
            padding: 60px var(--container-padding);
          }
          .roi-calc-grid {
            grid-template-columns: 1fr;
          }
          .roi-right {
            border-radius: 0 0 20px 20px;
          }
          .roi-salary-row {
            flex-wrap: wrap;
          }
          .roi-sal-btn {
            flex: 0 0 calc(50% - 4px);
          }
        }
      `}</style>
    </section>
  );
}
